import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { apiVariable } from "../../../api/apiVariable";
import useAuth from "../../../hooks/useAuth";
import {
  Box,
  Typography,
  CircularProgress,
  FormControlLabel,
  Switch,
  TextField,
} from "@mui/material";
import "./EmployeesTable.css";
import { employeesTableColumns } from "../../../data/FormColumns/HR/employeesTableColumns";
import { useNavigate } from "react-router-dom";
import PersistedDataGrid from "../../PersistedDataGrid/PersistedDataGrid";

const fetchEmployees = async (
  token: string | null | undefined,
  areActive: boolean,
) => {
  const { data } = await axios.get(`${apiVariable}/getAllEmployees`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    params: { areActive },
  });
  return data;
};

const EmployeesTable = () => {
  const { token } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");

  const [areActive, setAreActive] = useState(true);

  const navigate = useNavigate();
  const {
    data: employees,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ["employees"],
    queryFn: () => fetchEmployees(token, areActive),
  });

  useEffect(() => {
    if (token) {
      refetch();
    }
  }, [areActive, token, refetch]);

  const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAreActive(event.target.checked);
  };

  const filteredEmployees = useMemo(() => {
    if (!employees) return [];
    return employees.filter((employee: any) =>
      [employee.firstName, employee.lastName, employee.nickname].some((field) =>
        field?.toString().toLowerCase().includes(searchTerm.toLowerCase()),
      ),
    );
  }, [employees, searchTerm]);

  return (
    <Box className="employees-table-container" height={900}>
      <Box
        className="table-controls"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <FormControlLabel
          control={
            <Switch
              checked={areActive}
              onChange={handleSwitchChange}
              color="primary"
            />
          }
          label={`${areActive ? "Active" : "Inactive"} Employees`}
        />

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            position: "relative",
          }}
        >
          <TextField
            label="Search"
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by first name, last name"
            sx={{ maxWidth: "100%", width: 400 }}
          />
        </Box>
      </Box>
      {isLoading ? (
        <Box className="table-loader">
          <CircularProgress />
        </Box>
      ) : isError ? (
        <Box className="table-error">
          <Typography color="error">Error: {error.message}</Typography>
        </Box>
      ) : (
        <PersistedDataGrid
          tableName="employees"
          rows={filteredEmployees}
          columns={employeesTableColumns}
          compactViewConfig={{
            primaryField: "firstName",
            secondaryFields: ["position", "department"],
            detailFields: [
              "nickname",
              "legalPosition",
              "gender",
              "manager",
              "secondManager",
              "contractType",
              "startDate",
              "terminationDate",
              "email",
              "phoneNumber",
              "workPhoneNumber",
              "employeeId",
            ],
            actionFields: ["update", "delete"],
            getCardTitle: (row) =>
              [row.firstName, row.lastName].filter(Boolean).join(" ") ||
              "Unknown Employee",
          }}
          onRowClick={(row: any) => {
            navigate(`/hr/employee/${row.id}`);
          }}
          sx={{ height: 700 }}
        />
      )}
    </Box>
  );
};

export default EmployeesTable;
